<script setup>
import { useMenuStore } from "@/stores/menu";
import { useAuthStore } from "@/stores/auth";
import { useSettingsStore } from "@/stores/settings";
import { usePromotionStore } from "@/stores/promotion";


const route = useRoute();
const authStore = useAuthStore();
const menuStore = useMenuStore();
const settingsStore = useSettingsStore();
const promotionStore = usePromotionStore();
const runtimeConfig = useRuntimeConfig();

const isMobile = ref(isMobileDevice());

const settings = settingsStore.settings;
var generalMenu = settings.general_menu_site;
let sportsbookType = settings.sport_sportbook_type;

const openJivo = () => {
  jivo_api.open();
};

const sitesWp = [
  {
    api: 'https://app.pixpremioesporte.io',
    wp: 'https://wa.me/5534996588281'
  }
];

const handleClick = computed(() => {
  if (settingsStore.settings.general_live_chat !== '123456') {
    return menuStore.chat;
  } else if (sitesJivo.includes(runtimeConfig.public.API_URL)) {
    return openJivo;
  } else if (sitesWp.filter(site => site.api === runtimeConfig.public.API_URL)) {
    const matchedSite = sitesWp.find(site => site.api === runtimeConfig.public.API_URL);
    if (matchedSite) {
      return () => window.open(matchedSite.wp, '_blank');
    }
  } else {
    return '';
  }
});

const sitesJivo = [
  'https://backoffice.lendarias.com',
  'https://app.betizze.com',
  'https://app.bet3as.com'
];

if (generalMenu) {
  // Verifico se o menu é diferente de um array valido
  if (!Array.isArray(generalMenu)) {
    // Caso seja diferente, faço um map para transformar em pares enumerada de valores-chave
    generalMenu = Object.entries(settings.general_menu_site).map(
      ([name, menus]) => ({ name, menus }),
    );
  }
} else {
  generalMenu = [];
}

const isCollapsed = computed(() => menuStore.isCollapsed);
const expandMenu = () => {
  menuStore.isCollapsed = false;
};

const collapseMenuIfNeeded = () => {
  if (route.path.startsWith('/sports')) {
    menuStore.isCollapsed = true;
  }
};

onMounted(() => {
  window.addEventListener('resize', () => {
    isMobile.value = isMobileDevice();
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', () => {
    isMobile.value = isMobileDevice();
  });
});

watch(() => route.path, (newPath) => {
  if (newPath.startsWith('/sports')) {
    menuStore.isCollapsed = true;
  } else {
    menuStore.isCollapsed = false;
  }
}, { immediate: true });

</script>

<template>
  <div class="flex flex-col h-full" :class="isCollapsed ? 'w-20' : 'w-full sm:w-64'">
    <div class="relative flex flex-col flex-1 lg:border-r overflow-hidden" :class="[
      isCollapsed ? 'w-20' : 'w-full sm:w-64',
      'bg-brand-navbar sm:bg-brand-sidebar border-r border-brand-separator'
    ]">
      <!-- Logo and Close Button -->
      <div class="flex h-16 flex-row items-center justify-between px-6 sm:hidden">
        <div class="flex max-h-8">
          <BaseLogo />
        </div>
        <div @click="menuStore.toggle()" class="cursor-pointer text-bzMenuTextDark sm:absolute sm:right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-8 w-8">
            <path fill-rule="evenodd"
              d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z"
              clip-rule="evenodd" />
          </svg>
        </div>
      </div>

      <!-- Tabs Section (Cassino and Esportes) -->
      <div class="box-content min-h-16 flex sm:hidden border-b border-brand-separator bg-brand-navbar">
        <div class="flex h-full w-full translate-y-[1px] flex-row gap-x-2 px-2">
          <NuxtLink @click="menuStore.modalClose()" active-class="text-white" to="/"
            class="group relative flex grow cursor-pointer flex-row items-center justify-center gap-x-1 text-xs font-bold uppercase text-bzMenuText transition-all hover:text-white">
            <div>
              <Icon name="cil:casino" />
            </div>
            <div>Cassino</div>
            <div :class="[route.name === 'index' && 'max-h-0.5']"
              class="absolute bottom-0 left-0 right-0 flex h-2 max-h-0 bg-brand-primary transition-all group-hover:max-h-0.5">
            </div>
          </NuxtLink>

          <NuxtLink v-if="settings.cometa_sports_book" @click="menuStore.modalClose()" active-class="text-white"
            to="/sports"
            class="group relative flex grow cursor-pointer flex-row items-center justify-center gap-x-1 text-xs font-bold uppercase text-bzMenuText transition-all hover:text-white">
            <div>
              <Icon name="solar:football-bold" />
            </div>
            <div>Esportes</div>
            <div :class="[route.name === 'sports-page' && 'max-h-0.5']"
              class="absolute bottom-0 left-0 right-0 flex h-2 max-h-0 bg-brand-primary transition-all group-hover:max-h-0.5">
            </div>
          </NuxtLink>
        </div>
      </div>

      <!-- Promotions and Other Links -->
      <div class="scrollbar-hide flex-1 overflow-y-auto border-brand-separator lg:border-r">
        <div class="flex w-full items-center justify-center border-b border-brand-separator p-6"
          v-if="settings.refer_enabled && authStore.isLoggedIn" @click="menuStore.modalClose()">
          <NuxtLink to="/user/indications" class="w-full">
            <BaseButton
              class="text-extrabold w-full bg-brand-primary text-xs text-white flex items-center justify-center">
              <template v-if="!isCollapsed">
                💵 Ganhe {{ money(settings?.refer_referrer_amount / 100) }} Grátis
              </template>
              <template v-else>
                💵
              </template>
            </BaseButton>
          </NuxtLink>
        </div>
        <div class="scrollbar-hide flex-1 overflow-y-auto">
          <div class="flex flex-col text-white pb-14 sm:pb-0">
            <template v-for="(menus, index) in generalMenu" :key="index">
              <MenuItem>
              <template #header>
                <p v-if="!isCollapsed" class="text-white">{{ menus ? menus.name : "" }}</p>
              </template>
              <template #body>
                <template v-for="subMenus in menus" :key="subMenus">
                  <template v-for="groups in subMenus" :key="groups">
                    <p v-if="groups.title && !isCollapsed"
                      class="flex items-center justify-between text-sm font-bold uppercase text-white">
                      {{ groups ? groups.title : "" }}
                    </p>

                    <template v-for="item in groups.items" :key="item">
                      <MenuDropdownItem :to="item.link" v-if="item" :class="isCollapsed ? 'justify-center' : ''"
                        :title="item.name">
                        <Icon :name="item.icon" size="18" />
                        <p v-if="!isCollapsed">{{ item ? item.name : "" }}</p>
                      </MenuDropdownItem>
                    </template>
                  </template>
                </template>
              </template>
              </MenuItem>
            </template>

            <div v-show="isMobile" v-if="sportsbookType === 'altenar'" class="p-4">
              <AltenarFrameWidgetsWSportMenu />
            </div>

            <MenuItem class="gap-y-6">
            <template #body>
              <div class="flex h-auto w-full flex-col gap-6 pt-4">

                <MenuLink
                  v-if="settingsStore.settings.general_show_chat_sidebar"
                  class="mt-1 flex items-center"
                  active-class="text-brand"
                  @click="handleClick"
                  :class="isCollapsed ? 'justify-center' : 'justify-start'"
                  title="Suporte Ao Vivo"
                >
                  <Icon name="ph:chats-fill" size="21" />
                  <span v-if="!isCollapsed" class="ml-2">Suporte Ao Vivo</span>
                </MenuLink>

                <nuxt-link v-if="runtimeConfig.public.API_URL === 'https://backoffice.lotogreen.com'"
                  to="https://blog.lotogreen.com/" @click="menuStore.modalClose()" target="_blank">
                  <MenuLink active-class="text-brand-primary" :class="isCollapsed ? 'justify-center' : 'justify-start'"
                    class="flex items-center" title="Blog">
                    <Icon name="ph:globe-simple-fill" size="21" />
                    <span v-if="!isCollapsed" class="ml-2">Blog</span>
                  </MenuLink>
                </nuxt-link>

                <nuxt-link v-if="promotionStore.state.promotions.length > 0" to="/promotions"
                  @click="menuStore.modalClose()">
                  <MenuLink active-class="text-brand" :class="isCollapsed ? 'justify-center' : 'justify-start'"
                    class="flex items-center" title="Promoções">
                    <Icon name="ph:megaphone-simple-fill" size="21" />
                    <span v-if="!isCollapsed" class="ml-2">Promoções</span>
                  </MenuLink>
                </nuxt-link>

                <nuxt-link v-if="runtimeConfig.public.API_URL === 'https://risco.strike777.bet'"
                  to="https://strike777.partners/wp" @click="menuStore.modalClose()" target="_blank">
                  <MenuLink active-class="text-brand-primary" :class="isCollapsed ? 'justify-center' : 'justify-start'"
                    class="flex items-center" title="Blog">
                    <Icon name="ph:globe-simple-fill" size="21" />
                    <span v-if="!isCollapsed" class="ml-2">Blog</span>
                  </MenuLink>
                </nuxt-link>

                <nuxt-link v-if="runtimeConfig.public.API_URL === 'https://app.rei7.net'" to="https://blog.rei7.net"
                  @click="menuStore.modalClose()" target="_blank">
                  <MenuLink active-class="text-brand-primary" :class="isCollapsed ? 'justify-center' : 'justify-start'"
                    class="flex items-center" title="Blog">
                    <Icon name="ph:globe-simple-fill" size="21" />
                    <span v-if="!isCollapsed" class="ml-2">Blog</span>
                  </MenuLink>
                </nuxt-link>

                <nuxt-link v-if="runtimeConfig.public.API_URL === 'https://risco.strike777.bet'"
                  to="https://ajuda.strike777.bet" @click="menuStore.modalClose()" target="_blank">
                  <MenuLink active-class="text-brand-primary" :class="isCollapsed ? 'justify-center' : 'justify-start'"
                    class="flex items-center" title="Central de Ajuda">
                    <Icon name="ph:headset-fill" size="21" />
                    <span v-if="!isCollapsed" class="ml-2">Central de Ajuda</span>
                  </MenuLink>
                </nuxt-link>

              </div>
            </template>
            </MenuItem>

          </div>

          <!-- Load aqui!! -->
          <PageLoadersMenuLoaders v-if="false" />
        </div>
      </div>
    </div>
  </div>
</template>
